import React, { forwardRef, useEffect, useMemo, useRef, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment";
import styled, { createGlobalStyle } from "styled-components";
import { offset, shift } from "@floating-ui/react";
import { portalColors } from "./BusinessPortalComponents";

const DATE_PICKER_MASK = "url(\"data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 width=%2216%22 height=%2216%22 viewBox=%220 0 24 24%22 fill=%22none%22 stroke=%22black%22 stroke-width=%222%22 stroke-linecap=%22round%22 stroke-linejoin=%22round%22%3E%3Crect x=%223%22 y=%224%22 width=%2218%22 height=%2218%22 rx=%222%22 ry=%222%22/%3E%3Cline x1=%2216%22 y1=%222%22 x2=%2216%22 y2=%226%22/%3E%3Cline x1=%228%22 y1=%222%22 x2=%228%22 y2=%226%22/%3E%3Cline x1=%223%22 y1=%2210%22 x2=%2221%22 y2=%2210%22/%3E%3C/svg%3E\") center / contain no-repeat";
const TIME_PICKER_MASK = "url(\"data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 width=%2216%22 height=%2216%22 viewBox=%220 0 24 24%22 fill=%22none%22 stroke=%22black%22 stroke-width=%222%22 stroke-linecap=%22round%22 stroke-linejoin=%22round%22%3E%3Ccircle cx=%2212%22 cy=%2212%22 r=%229%22/%3E%3Cpolyline points=%2212 7 12 12 15 15%22/%3E%3C/svg%3E\") center / contain no-repeat";

const datePickerPopperProps = {
    middleware: [
        offset(10),
        shift({ padding: 8 })
    ]
};

export const PickerStyles = createGlobalStyle`
    .business-portal-datepicker {
        border: 1px solid ${portalColors.borderStrong};
        border-radius: 8px;
        box-shadow: ${portalColors.shadow};
        overflow: hidden;
        font-family: Manrope, Helvetica, sans-serif;
        background: ${portalColors.surface};
        color: ${portalColors.text};
    }

    .business-portal-datepicker .react-datepicker__header {
        background: ${portalColors.surfaceSoft};
        border-bottom: 1px solid ${portalColors.border};
    }

    .business-portal-datepicker .react-datepicker__current-month,
    .business-portal-datepicker .react-datepicker-time__header {
        color: ${portalColors.text};
        font-size: 0.938rem;
        font-weight: 800;
    }

    .business-portal-datepicker .react-datepicker__day-name,
    .business-portal-datepicker .react-datepicker__day,
    .business-portal-datepicker .react-datepicker__time-name {
        color: ${portalColors.text};
    }

    .business-portal-datepicker .react-datepicker__day--keyboard-selected,
    .business-portal-datepicker .react-datepicker__day--selected {
        background: ${portalColors.blue};
        color: var(--calendar-bg, #fff);
    }

    .business-portal-datepicker .react-datepicker__day:hover,
    .business-portal-datepicker .react-datepicker__month-text:hover,
    .business-portal-datepicker .react-datepicker__quarter-text:hover,
    .business-portal-datepicker .react-datepicker__year-text:hover {
        background: ${portalColors.blueSoft};
        color: ${portalColors.text};
    }

    .business-portal-datepicker-input .react-datepicker-wrapper,
    .business-portal-datepicker-input .react-datepicker__input-container {
        display: block;
        width: 100%;
    }

    .react-datepicker-popper {
        z-index: 1100;
    }
`;

export const PickerFieldWrap = styled.div`
    position: relative;
    width: 100%;
`;

export const PickerFieldIcon = styled.span`
    position: absolute;
    right: 14px;
    top: 50%;
    width: 16px;
    height: 16px;
    transform: translateY(-50%);
    pointer-events: none;
    z-index: 2;
    background-color: ${portalColors.textMuted};
    mask: ${p => p.$icon === "time" ? TIME_PICKER_MASK : DATE_PICKER_MASK};
    -webkit-mask: ${p => p.$icon === "time" ? TIME_PICKER_MASK : DATE_PICKER_MASK};
`;

export const PickerFieldInput = styled.input`
    width: 100%;
    height: 46px;
    border: ${p => p.$isError ? "1px solid #D64545" : `1px solid ${portalColors.borderStrong}`};
    border-radius: 8px;
    background-color: ${p => p.disabled ? portalColors.surfaceTertiary : p.$backgroundColor || portalColors.surface};
    color: ${portalColors.text};
    -webkit-text-fill-color: ${portalColors.text};
    padding: 0 2.9rem 0 1rem;
    box-sizing: border-box;
    outline: none;
    box-shadow: none;
    font-family: Manrope, Helvetica, sans-serif;
    font-size: ${p => p.$size || "0.875rem"};
    font-weight: ${p => p.$fontWeight || "600"};
    line-height: 1.2;
    text-align: left;
    cursor: ${p => p.disabled ? "not-allowed" : "pointer"};
    user-select: none;

    ::placeholder {
        color: ${portalColors.textSoft};
        -webkit-text-fill-color: ${portalColors.textSoft};
        font-weight: 400;
        opacity: 1;
    }

    &:focus,
    &:focus-visible {
        border-color: ${portalColors.text};
        box-shadow: 0 0 0 4px ${portalColors.blueSoft};
        background-color: ${p => p.disabled ? portalColors.surfaceTertiary : p.$backgroundColor || portalColors.surface};
    }

    &[readonly] {
        cursor: ${p => p.disabled ? "not-allowed" : "pointer"};
    }
`;

export const PickerFieldGroup = styled.div`
    display: grid;
    grid-template-columns: ${p => p.$columns || "minmax(13.25rem, 1.35fr) minmax(8.75rem, 0.65fr)"};
    gap: 0.75rem;
    width: 100%;

    @media (max-width: 420px) {
        grid-template-columns: 1fr;
    }
`;

export const TimePickerWrap = styled.div`
    position: relative;
    width: 100%;
`;

export const TimePickerPanel = styled.div`
    position: absolute;
    left: 0;
    right: 0;
    top: ${p => p.$placement === "top" ? "auto" : "calc(100% + 8px)"};
    bottom: ${p => p.$placement === "top" ? "calc(100% + 8px)" : "auto"};
    z-index: 1000;
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.25rem;
    max-height: 240px;
    overflow-y: auto;
    padding: 0.45rem;
    border: 1px solid ${portalColors.borderStrong};
    border-radius: 8px;
    background: ${portalColors.surface};
    box-shadow: ${portalColors.shadow};
`;

export const TimePickerOption = styled.button`
    min-height: 32px;
    border-radius: 6px;
    border: 1px solid ${p => p.$active ? portalColors.text : "transparent"};
    background: ${p => p.$active ? portalColors.blue : "transparent"};
    color: ${p => p.$active ? "var(--calendar-bg, #fff)" : portalColors.text};
    cursor: pointer;
    font-family: Manrope, Helvetica, sans-serif;
    font-size: 12px;
    font-weight: 700;

    &:hover {
        border-color: ${portalColors.borderStrong};
        background: ${p => p.$active ? portalColors.blue : portalColors.blueSoft};
        color: ${p => p.$active ? "var(--calendar-bg, #fff)" : portalColors.text};
    }
`;

export const TIME_PICKER_OPTIONS = Array.from({ length: 96 }, (_, index) => {
    const totalMinutes = index * 15;
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    const value = `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
    return {
        value,
        label: moment(value, "HH:mm").format("h:mm A")
    };
});

const toDate = (value) => {
    if (!value) return null;
    if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
    if (moment.isMoment(value)) return value.isValid() ? value.toDate() : null;
    const parsed = moment(value);
    return parsed.isValid() ? parsed.toDate() : null;
};

const toTimeValue = (value) => {
    if (!value) return "";
    const parsed = moment(value, ["HH:mm", "H:mm", "h:mm A"], true);
    return parsed.isValid() ? parsed.format("HH:mm") : "";
};

const formatDateLabel = (value) => {
    const date = toDate(value);
    return date ? moment(date).format("Do MMMM, YYYY") : "";
};

const formatTimeLabel = (value) => {
    const timeValue = toTimeValue(value);
    return timeValue ? moment(timeValue, "HH:mm").format("h:mm A") : "";
};

const getDefaultTime = (date) => {
    const base = date ? moment(date) : moment();
    const roundedMinutes = Math.ceil(base.minute() / 15) * 15;
    return base.minute(roundedMinutes).second(0).millisecond(0).format("HH:mm");
};

const combineDateAndTime = (dateValue, timeValue) => {
    const date = toDate(dateValue);
    if (!date) return null;
    const time = toTimeValue(timeValue) || getDefaultTime(date);
    const [hours, minutes] = time.split(":").map(Number);
    return moment(date).hour(hours).minute(minutes).second(0).millisecond(0).toDate();
};

const DatePickerCustomInput = forwardRef(({ disabled, displayValue, isError, placeholder = "Select date", size, fontWeight, backgroundColor, inputStyle, onClick }, ref) => (
    <PickerFieldWrap>
        <PickerFieldInput
            ref={ref}
            type="text"
            readOnly
            disabled={disabled}
            value={displayValue}
            placeholder={placeholder}
            $size={size}
            $fontWeight={fontWeight}
            $backgroundColor={backgroundColor}
            $isError={isError}
            style={inputStyle}
            onClick={disabled ? undefined : onClick}
        />
        <PickerFieldIcon $icon="date" />
    </PickerFieldWrap>
));

DatePickerCustomInput.displayName = "DatePickerCustomInput";

export function SharedDateInput({
    value,
    onChange,
    placeholder = "Select date",
    disabled = false,
    minDate = undefined,
    isError = false,
    size = undefined,
    fontWeight = undefined,
    backgroundColor = undefined,
    inputStyle = undefined,
    popperPlacement = "bottom-start",
    onCalendarOpen,
    onCalendarClose,
}) {
    const selectedDate = toDate(value);

    return (
        <div className="business-portal-datepicker-input">
            <PickerStyles />
            <DatePicker
                selected={selectedDate}
                max="9999-12-31"
                minDate={minDate ? toDate(minDate) : undefined}
                onChange={date => onChange(date)}
                disabled={disabled}
                calendarClassName="business-portal-datepicker"
                placeholderText={placeholder}
                popperPlacement={popperPlacement}
                popperProps={datePickerPopperProps}
                onCalendarOpen={onCalendarOpen}
                onCalendarClose={onCalendarClose}
                showPopperArrow={false}
                customInput={(
                    <DatePickerCustomInput
                        disabled={disabled}
                        displayValue={formatDateLabel(value)}
                        isError={isError}
                        placeholder={placeholder}
                        size={size}
                        fontWeight={fontWeight}
                        backgroundColor={backgroundColor}
                        inputStyle={inputStyle}
                    />
                )}
            />
        </div>
    );
}

export function SharedTimeInput({
    value,
    onChange,
    placeholder = "Select time",
    disabled = false,
    isError = false,
    size = undefined,
    fontWeight = undefined,
    backgroundColor = undefined,
    inputStyle = undefined,
    placement = "bottom",
}) {
    const [isOpen, setIsOpen] = useState(false);
    const wrapRef = useRef(null);
    const timeValue = toTimeValue(value);

    useEffect(() => {
        if (!isOpen) return undefined;

        const handleDocumentMouseDown = (event) => {
            if (wrapRef.current?.contains(event.target)) return;
            setIsOpen(false);
        };

        document.addEventListener("mousedown", handleDocumentMouseDown);
        return () => document.removeEventListener("mousedown", handleDocumentMouseDown);
    }, [isOpen]);

    return (
        <TimePickerWrap ref={wrapRef}>
            <PickerFieldWrap>
                <PickerFieldInput
                    type="text"
                    readOnly
                    disabled={disabled}
                    value={formatTimeLabel(timeValue)}
                    placeholder={placeholder}
                    $size={size}
                    $fontWeight={fontWeight}
                    $backgroundColor={backgroundColor}
                    $isError={isError}
                    style={inputStyle}
                    onClick={disabled ? undefined : () => setIsOpen(open => !open)}
                />
                <PickerFieldIcon $icon="time" />
            </PickerFieldWrap>
            {isOpen && (
                <TimePickerPanel $placement={placement}>
                    {TIME_PICKER_OPTIONS.map(option => (
                        <TimePickerOption
                            key={option.value}
                            type="button"
                            $active={timeValue === option.value}
                            onClick={() => {
                                onChange(option.value);
                                setIsOpen(false);
                            }}
                        >
                            {option.label}
                        </TimePickerOption>
                    ))}
                </TimePickerPanel>
            )}
        </TimePickerWrap>
    );
}

export function SharedDateTimeInput({
    value,
    onChange,
    placeholder = "Select date",
    timePlaceholder = "Select time",
    disabled = false,
    minDate = undefined,
    isError = false,
    size = undefined,
    fontWeight = undefined,
    backgroundColor = undefined,
    inputStyle = undefined,
    style = undefined,
    datePickerPlacement = "bottom-start",
    timePickerPlacement = "bottom",
    columns = undefined,
    onCalendarOpen,
    onCalendarClose,
}) {
    const selectedDate = toDate(value);
    const selectedTime = useMemo(() => selectedDate ? moment(selectedDate).format("HH:mm") : "", [selectedDate]);

    const handleDateChange = (date) => {
        onChange(combineDateAndTime(date, selectedTime));
    };

    const handleTimeChange = (time) => {
        onChange(combineDateAndTime(selectedDate || minDate || new Date(), time));
    };

    return (
        <PickerFieldGroup style={style} $columns={columns}>
            <SharedDateInput
                value={selectedDate}
                onChange={handleDateChange}
                placeholder={placeholder}
                disabled={disabled}
                minDate={minDate}
                isError={isError}
                size={size}
                fontWeight={fontWeight}
                backgroundColor={backgroundColor}
                inputStyle={inputStyle}
                popperPlacement={datePickerPlacement}
                onCalendarOpen={onCalendarOpen}
                onCalendarClose={onCalendarClose}
            />
            <SharedTimeInput
                value={selectedTime}
                onChange={handleTimeChange}
                placeholder={timePlaceholder}
                disabled={disabled}
                isError={isError}
                size={size}
                fontWeight={fontWeight}
                backgroundColor={backgroundColor}
                inputStyle={inputStyle}
                placement={timePickerPlacement}
            />
        </PickerFieldGroup>
    );
}

export { toDate as toPickerDate, toTimeValue, formatDateLabel, formatTimeLabel };
